import { useEffect, useMemo, useState } from 'react';
import type { BackgroundWorkItem } from '@localagent/sdk';

export const RECENT_WINDOW_MS = 10 * 60 * 1000;
export const RECENT_LIMIT = 3;


export interface VisibleRunningWork {
  running: BackgroundWorkItem[];
  recent: BackgroundWorkItem[];
  now: number;
}

export function ms(at: string | null): number | null {
  return at === null ? null : Date.parse(at);
}

export function formatElapsed(fromMs: number, nowMs: number): string {
  const seconds = Math.max(0, Math.floor((nowMs - fromMs) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return minutes % 60 === 0 ? `${hours}h` : `${hours}h ${minutes % 60}m`;
}

export function visibleRunningWork(items: readonly BackgroundWorkItem[], now: number): VisibleRunningWork {
  const running = items.filter((i) => i.status === 'running');
  const recent = items
    .filter((i) => {
      const ended = ms(i.endedAt);
      return i.status !== 'running' && ended !== null && now - ended <= RECENT_WINDOW_MS;
    })
    .slice(0, RECENT_LIMIT);
  return { running, recent, now };
}

export function useVisibleRunningWork(items: readonly BackgroundWorkItem[], fixedNow?: number): VisibleRunningWork {
  const [now, setNow] = useState(() => fixedNow ?? Date.now());
  const youngest = useMemo(() => {
    const starts = items.filter((i) => i.status === 'running').map((i) => ms(i.startedAt)).filter((t): t is number => t !== null);
    return starts.length === 0 ? null : Math.max(...starts);
  }, [items]);
  const fast = youngest !== null && now - youngest < 60_000;
  useEffect(() => {
    if (fixedNow !== undefined || items.length === 0) return;
    const timer = setInterval(() => setNow(Date.now()), fast ? 1000 : 60_000);
    return () => clearInterval(timer);
  }, [fixedNow, items.length, fast]);
  return visibleRunningWork(items, fixedNow ?? now);
}
